/**
 * RevealOnceModal - shows a secret that will never be retrievable again.
 *
 * Used after creating an API key (`raw_key`) and after creating a webhook
 * endpoint (`secret`). Renders the value in a monospace box, offers a
 * copy-to-clipboard button, and a prominent warning. The caller controls
 * the open / close state; closing the modal is the only way to dismiss
 * the value and there is no other surface that displays it.
 */
import { useState } from "react";

import { Alert, Button, Modal } from "@/components/ui";

export interface RevealOnceModalProps {
  /** Whether the modal is visible. */
  isOpen: boolean;
  /** Called when the user dismisses the dialog. */
  onClose: () => void;
  /** Dialog title (e.g. "API key created"). */
  title: string;
  /**
   * Short context line shown above the secret box. Tell the user what
   * the secret is for - e.g. "Use this Authorization: Bearer token".
   */
  description: string;
  /** The cleartext value to reveal. */
  secret: string;
  /**
   * Optional override for the close-button label. Defaults to
   * "I've captured the secret".
   */
  closeLabel?: string;
}

export function RevealOnceModal({
  isOpen,
  onClose,
  title,
  description,
  secret,
  closeLabel,
}: RevealOnceModalProps): JSX.Element {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable - leave the visible secret for manual copy.
      setCopied(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="lg">
      <div className="space-y-4">
        <Alert variant="warning" title="This value is shown only once">
          Capture it now. Buyist Support stores only a hash and will not be
          able to reveal it again. If you lose it, rotate the credential and
          create a new one.
        </Alert>

        <p className="text-sm text-neutral-700">{description}</p>

        <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3">
          <code
            data-testid="reveal-once-secret"
            className="block break-all font-mono text-sm text-neutral-900"
          >
            {secret}
          </code>
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              void handleCopy();
            }}
          >
            <i
              className="fa-duotone fa-thin fa-copy"
              aria-hidden="true"
            />
            {copied ? "Copied" : "Copy to clipboard"}
          </Button>
          <Button type="button" onClick={onClose}>
            {closeLabel ?? "I've captured the secret"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default RevealOnceModal;
